import { FieldValues } from 'react-hook-form';
import { PerilUpdateRequest } from '../../../types/policy';
import {
	PERIL_FIELD_NAMES,
	PERIL_FIELD_UPDATE_VALUES,
	PERIL_FIELD_VALUES,
} from '../data/constants';
import { DirtyFields, FormBuilder, FormShape } from '../types';
import filterObjectByKeys from './filterObjectByKeys';
import updateRequestBuilder from './updateRequestBuilder';

const PERIL_SPECIAL_FIELDS = [
	PERIL_FIELD_NAMES.indemnityPerItemType,
	PERIL_FIELD_NAMES.indemnityPerItem,
	PERIL_FIELD_NAMES.indemnityPerEventType,
	PERIL_FIELD_NAMES.indemnityPerEvent,
	PERIL_FIELD_NAMES.indemnityPerAggregateType,
	PERIL_FIELD_NAMES.indemnityPerAggregate,
	PERIL_FIELD_NAMES.lumpSumPaymentType,
	PERIL_FIELD_NAMES.lumpSumPayment,
	PERIL_FIELD_NAMES.deductibleType,
	PERIL_FIELD_NAMES.deductible,
];

const updatePerilRequestBuilder = (
	dirtyFields: { [key: string]: DirtyFields },
	formValues: FieldValues,
	fields: FormShape<FormBuilder>,
	key: string,
	timezone: string,
	isEndorsementOrRenewal = false,
	hiddenPerilLimits = false,
) => {
	let updateObject = {} as PerilUpdateRequest;

	if (Object.keys(dirtyFields[key] || {}).length > 0) {
		let newUpdatedObject;
		const currDirtyFields = { [key]: dirtyFields[key] || {} };
		const currFormValues = { [key]: formValues[key] || {} };
		const currFields = {
			[key as keyof FormBuilder]: fields[key] || {},
		} as FormShape<FormBuilder>;

		Object.keys(currDirtyFields[key]).forEach((field) => {
			if (currDirtyFields[key][field] === false) {
				return;
			}
			// if peril limits hidden, do not add limit values to payload
			if (hiddenPerilLimits) {
				return;
			}

			switch (field) {
				case PERIL_FIELD_NAMES.indemnityPerItemType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityPerItem = PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityPerItem = true;
					} else {
						updateObject.indemnityPerItem =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerItem];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerEventType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityPerEvent =
							PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityPerEvent = true;
					} else {
						updateObject.indemnityPerEvent =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerEvent];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerAggregateType:
					if (currFormValues[key][field] === PERIL_FIELD_VALUES.unlimited) {
						updateObject.indemnityInAggregate =
							PERIL_FIELD_UPDATE_VALUES.unlimited;
					} else if (currFormValues[key][field] === PERIL_FIELD_VALUES.unset) {
						updateObject.removeIndemnityInAggregate = true;
					} else {
						updateObject.indemnityInAggregate =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerAggregate];
					}
					break;
				case PERIL_FIELD_NAMES.lumpSumPaymentType:
					if (
						currFormValues[key][PERIL_FIELD_NAMES.lumpSumPaymentType] ===
						PERIL_FIELD_VALUES.set
					) {
						updateObject.lumpSumPayment =
							currFormValues[key][PERIL_FIELD_NAMES.lumpSumPayment];
					} else {
						updateObject.removeLumpSumPayment = true;
					}
					break;
				case PERIL_FIELD_NAMES.deductibleType:
					if (
						currFormValues[key][PERIL_FIELD_NAMES.deductibleType] ===
						PERIL_FIELD_VALUES.set
					) {
						updateObject.deductible =
							currFormValues[key][PERIL_FIELD_NAMES.deductible];
					} else {
						updateObject.removeDeductible = true;
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerItem:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerItemType]) {
						updateObject.indemnityPerItem =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerItem];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerEvent:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerEventType]) {
						updateObject.indemnityPerEvent =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerEvent];
					}
					break;
				case PERIL_FIELD_NAMES.indemnityPerAggregate:
					if (
						!currDirtyFields[key][PERIL_FIELD_NAMES.indemnityPerAggregateType]
					) {
						updateObject.indemnityInAggregate =
							currFormValues[key][PERIL_FIELD_NAMES.indemnityPerAggregate];
					}
					break;
				case PERIL_FIELD_NAMES.deductible:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.deductibleType]) {
						updateObject.deductible =
							currFormValues[key][PERIL_FIELD_NAMES.deductible];
					}
					break;
				case PERIL_FIELD_NAMES.lumpSumPayment:
					if (!currDirtyFields[key][PERIL_FIELD_NAMES.lumpSumPaymentType]) {
						updateObject.lumpSumPayment =
							currFormValues[key][PERIL_FIELD_NAMES.lumpSumPayment];
					}
					break;
				default:
					break;
			}
		});

		const filteredDirtyFields = {
			[key]: filterObjectByKeys(
				currDirtyFields[key],
				hiddenPerilLimits ? [] : PERIL_SPECIAL_FIELDS,
			),
		};

		newUpdatedObject = updateRequestBuilder<PerilUpdateRequest>(
			filteredDirtyFields,
			currFormValues,
			currFields,
			key,
			timezone,
			false,
			isEndorsementOrRenewal,
		);

		// remove any peril-specific limit fields that may have been set in fieldValues object if hiddenPerilLimits is false
		if (!hiddenPerilLimits) {
			for (const perilSpecialField of PERIL_SPECIAL_FIELDS) {
				newUpdatedObject &&
					newUpdatedObject.fieldValues &&
					delete newUpdatedObject.fieldValues[perilSpecialField];
			}
		}

		updateObject = { ...updateObject, ...newUpdatedObject };
	}

	const { updateFieldGroups, removeFieldGroups, addFieldGroups, fieldValues } =
		updateObject;
	if (updateFieldGroups && updateFieldGroups.length === 0) {
		delete updateObject.updateFieldGroups;
	}
	if (addFieldGroups && addFieldGroups.length === 0) {
		delete updateObject.addFieldGroups;
	}
	if (removeFieldGroups && removeFieldGroups.length === 0) {
		delete updateObject.removeFieldGroups;
	}
	if (fieldValues && Object.keys(fieldValues).length === 0) {
		delete updateObject.fieldValues;
	}

	return updateObject;
};

export default updatePerilRequestBuilder;
